package no.vareflow.web

import no.vareflow.app.domain.*
import no.vareflow.app.importer.*

/**
 * Produkter → Importer varer (admin): an Excel (.xlsx) or CSV file becomes products. The shared
 * ProductImport finds the columns and the hovedvarer; here the admin checks the preview (sheet,
 * columns, where the hovedvare comes from, renames, duplicates) before anything is saved.
 */
fun WebApp.showProductImport(user: UserAccount) {
    val state = object {
        var fileName = ""
        var sheets: List<Pair<SheetData, Int>> = emptyList()
        var sheet: SheetData? = null
        var settings: ImportSettings? = null
        var basePlan: ImportPlan? = null
        var plan: ImportPlan? = null
        /** Hovedvare renames: key of the name in the file → new name. */
        val renames = LinkedHashMap<String, String>()
        var showColumns = false
        var showSkipped = false
        var busy = false
        var error: String? = null
    }

    fun recompute() {
        val sheet = state.sheet ?: return
        val base = state.settings ?: return
        state.basePlan = ProductImport.plan(sheet, base.copy(categoryRenames = emptyMap()), core.products, core.orderedCategories(), state.fileName)
        state.plan = ProductImport.plan(sheet, base.copy(categoryRenames = state.renames.toMap()), core.products, core.orderedCategories(), state.fileName)
    }

    fun chooseSheet(sheet: SheetData) {
        state.sheet = sheet
        state.renames.clear()
        state.settings = ProductImport.suggestSettings(sheet)
        state.error = if (state.settings == null) t("Fant ingen overskriftsrad med «Varenavn» i arket «${sheet.name}». Velg et annet ark.",
            "Found no header row with «Product name» in the sheet «${sheet.name}». Choose another sheet.") else null
        state.plan = null
        state.basePlan = null
        recompute()
    }

    fun load(file: dynamic) {
        state.busy = true
        state.error = null
        requestRender()
        val name = (file.name as? String).orEmpty()
        val reader: dynamic = js("new FileReader()")
        reader.onload = { _: dynamic ->
            try {
                val bytes: ByteArray = js("new Int8Array(reader.result)").unsafeCast<ByteArray>()
                val sheets = ProductImport.readFile(name, bytes)
                state.fileName = name
                state.sheets = ProductImport.rankSheets(sheets)
                chooseSheet(state.sheets.first().first)
            } catch (e: Throwable) {
                state.error = t("Kunne ikke lese filen: ", "Could not read the file: ") + (e.message ?: e.toString())
                state.sheets = emptyList(); state.sheet = null; state.plan = null
            }
            state.busy = false
            requestRender()
        }
        reader.onerror = { _: dynamic -> state.busy = false; state.error = t("Kunne ikke lese filen.", "Could not read the file."); requestRender() }
        reader.readAsArrayBuffer(file)
    }

    openDialog(t("Importer varer", "Import products")) { close ->
        div("import-dialog") {
            p(t("Velg en Excel-fil (.xlsx) eller CSV med en rad per vare. Vareflow finner kolonnene (varenavn, varenummer, kategori, enhet, pris, MVA, leverandør) og hovedvarene selv – du ser alt før noe lagres.",
                "Choose an Excel file (.xlsx) or CSV with one row per product. Vareflow finds the columns (name, number, category, unit, price, VAT, supplier) and the main categories itself – you see everything before anything is saved."), "muted small")
            div("button-row") {
                el("label", "btn outline file-button") {
                    span("", if (state.fileName.isEmpty()) t("Velg fil …", "Choose file …") else t("Velg en annen fil …", "Choose another file …"))
                    el("input") {
                        attr("type", "file")
                        attr("data-key", "import-file")
                        attr("accept", ".xlsx,.csv,.txt,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv")
                        on("change") {
                            val files = node.files
                            val file: dynamic = if (files != null && (files.length as Int) > 0) files[0] else null
                            if (file != null) load(file)
                            node.value = ""
                        }
                    }
                }
                if (state.fileName.isNotEmpty()) span("strong small import-file-name", state.fileName)
            }
            if (state.busy) p(t("Leser filen …", "Reading the file …"), "muted small")
            errorText(state.error)

            val sheet = state.sheet
            val settings = state.settings
            if (state.sheets.size > 1 && sheet != null) {
                div("field") {
                    span("field-label", t("Ark", "Sheet"))
                    selectBox(this@showProductImport, "import-sheet",
                        state.sheets.map { (s, count) -> s.name to "${s.name} (" + (if (count == 1) t("1 vare", "1 product") else t("$count varer", "$count products")) + ")" },
                        sheet.name, t("Ark", "Sheet")) { name ->
                        state.sheets.firstOrNull { it.first.name == name }?.let { chooseSheet(it.first) }
                    }
                }
            }
            val plan = state.plan
            if (sheet == null || settings == null || plan == null) {
                dialogActions { button(t("Avbryt", "Cancel"), "btn text") { close() } }
                return@div
            }

            // Columns: what was found, changeable.
            div("import-section") {
                div("row") {
                    div("grow strong small", t("Kolonner", "Columns"))
                    button(if (state.showColumns) t("Skjul", "Hide") else t("Endre", "Change"), "btn text small") { state.showColumns = !state.showColumns; requestRender() }
                }
                val headerCells = sheet.rows.getOrNull(settings.headerRow).orEmpty()
                fun headerName(col: Int) = headerCells.getOrNull(col)?.text?.takeIf { it.isNotBlank() } ?: columnLetter(col)
                if (!state.showColumns) {
                    div("muted small", ImportField.entries.mapNotNull { f -> settings.columns[f]?.let { "${f.label} ← «${headerName(it)}»" } }.joinToString(" · "))
                    val missing = ImportField.entries.filter { it !in settings.columns }
                    if (missing.isNotEmpty()) div("muted tiny", t("Ikke funnet: ", "Not found: ") + missing.joinToString(", ") { it.label })
                } else {
                    div("form-grid") {
                        ImportField.entries.forEach { f ->
                            div("field") {
                                span("field-label", f.label)
                                val width = sheet.rows.drop(settings.headerRow).take(40).maxOfOrNull { it.size } ?: headerCells.size
                                val colOptions = listOf("-1" to t("— ingen —", "— none —")) +
                                    (0 until maxOf(width, headerCells.size)).map { c -> c.toString() to "${columnLetter(c)}: ${headerName(c)}" }
                                selectBox(this@showProductImport, "import-col-${f.name}", colOptions, (settings.columns[f] ?: -1).toString(), f.label) { value ->
                                        val col = value.toInt()
                                        val cols = settings.columns.toMutableMap()
                                        if (col < 0) cols.remove(f) else cols[f] = col
                                        if (ImportField.NAME !in cols) { state.error = t("Varenavn må ha en kolonne.", "The product name needs a column."); requestRender(); return@selectBox }
                                        state.error = null
                                        var next = settings.copy(columns = cols)
                                        if (next.categorySource == CategorySource.COLUMN && ImportField.CATEGORY !in cols) next = next.copy(categorySource = CategorySource.SINGLE)
                                        state.settings = next
                                        recompute()
                                        requestRender()
                                }
                            }
                        }
                    }
                }
            }

            // Where the hovedvare comes from.
            div("import-section") {
                div("strong small", t("Hovedvare hentes fra", "Main category comes from"))
                div("chip-row wrap") {
                    CategorySource.entries.forEach { source ->
                        val available = when (source) {
                            CategorySource.COLUMN -> ImportField.CATEGORY in settings.columns
                            CategorySource.HEADINGS -> plan.headings.isNotEmpty()
                            CategorySource.SINGLE -> true
                        }
                        if (available) chip(source.label, settings.categorySource == source) {
                            state.settings = settings.copy(categorySource = source)
                            state.renames.clear()
                            recompute()
                            requestRender()
                        }
                    }
                }
                when (settings.categorySource) {
                    CategorySource.COLUMN -> if (plan.headings.isNotEmpty()) div("muted tiny", t("Overskriftsradene (${plan.headings.take(4).joinToString()}${if (plan.headings.size > 4) " …" else ""}) lagres som undergruppe på varen.",
                        "The heading rows (${plan.headings.take(4).joinToString()}${if (plan.headings.size > 4) " …" else ""}) are kept as a subgroup on the product."))
                    CategorySource.HEADINGS -> if (ImportField.CATEGORY in settings.columns) div("muted tiny", t("Kategori-kolonnen lagres som undergruppe på varen.", "The category column is kept as a subgroup on the product."))
                    CategorySource.SINGLE -> field(t("Navn på hovedvaren", "Name of the main category"), settings.singleCategory, "import-single") { v ->
                        state.settings = state.settings?.copy(singleCategory = v)
                        recompute()
                    }.also { it.on("change") { requestRender() } }
                }
            }

            // Hovedvarer with counts; renaming merges into an existing one when the names match.
            val base = state.basePlan ?: plan
            div("import-section") {
                div("strong small", t("Hovedvarer (${plan.categories.size})", "Main categories (${plan.categories.size})"))
                div("import-categories") {
                    base.categories.forEach { original ->
                        val k = ProductImport.key(original.name)
                        val current = state.renames[k] ?: original.name
                        val summary = plan.categories.firstOrNull { ProductImport.key(it.name) == ProductImport.key(current) }
                        val undergrupper = summary?.undergrupper.orEmpty()
                        div("import-category") {
                            input(current, "import-cat-$k", "input") { v ->
                                if (v.isBlank() || v.trim() == original.name) state.renames.remove(k) else state.renames[k] = v
                                recompute()
                            }.attr("aria-label", t("Navn på hovedvaren", "Name of the main category")).also { it.on("change") { requestRender() } }
                            span("muted small", if (original.count == 1) t("1 vare", "1 product") else t("${original.count} varer", "${original.count} products"))
                            if (summary != null) statusBadge(if (summary.isNew) t("NY", "NEW") else t("FINNES", "EXISTS"), if (summary.isNew) Tone.INDIGO else Tone.GREEN)
                        }
                        if (undergrupper.isNotEmpty()) div("muted tiny import-undergrupper",
                            t("Undergrupper: ", "Subgroups: ") + undergrupper.joinToString(" · ") { (ug, n) -> "$ug ($n)" })
                    }
                }
                if (settings.categorySource != CategorySource.HEADINGS && plan.headings.isNotEmpty()) {
                    checkbox(t("Overskriftene i en hovedvare blir undergrupper (f.eks. Vin → Rødvin, Hvitvin)", "Headings inside a main category become subgroups (e.g. Wine → Red, White)"),
                        settings.useUndergruppe, "import-use-ug") { on ->
                        state.settings = state.settings?.copy(useUndergruppe = on)
                        recompute()
                        requestRender()
                    }
                }
                div("muted tiny", t("Nye hovedvarer opprettes automatisk. Skriv navnet på en eksisterende hovedvare for å legge varene der.",
                    "New main categories are created automatically. Type the name of an existing main category to put the products there."))
            }

            // Products already in Vareflow.
            val matches = plan.updates.size + plan.unchanged.size
            if (matches > 0) div("import-section") {
                div("strong small", t("$matches varer finnes allerede (samme varenummer og navn)", "$matches products already exist (same number and name)"))
                div("chip-row wrap") {
                    chip(t("Oppdater pris, enhet, MVA og leverandør", "Update price, unit, VAT and supplier"), settings.duplicates == DuplicateMode.UPDATE) {
                        state.settings = settings.copy(duplicates = DuplicateMode.UPDATE); recompute(); requestRender()
                    }
                    chip(t("La dem være", "Leave them"), settings.duplicates == DuplicateMode.SKIP) {
                        state.settings = settings.copy(duplicates = DuplicateMode.SKIP); recompute(); requestRender()
                    }
                }
            }

            // Summary and a sample.
            div("import-summary") {
                attr("role", "status")
                div("strong", t("${plan.newProducts.size} nye varer", "${plan.newProducts.size} new products") +
                    (if (plan.updates.isNotEmpty()) t(" · ${plan.updates.size} oppdateres", " · ${plan.updates.size} updated") else "") +
                    (if (plan.unchanged.isNotEmpty()) t(" · ${plan.unchanged.size} uendret", " · ${plan.unchanged.size} unchanged") else "") +
                    (if (plan.skipped.isNotEmpty()) t(" · ${plan.skipped.size} rader hoppes over", " · ${plan.skipped.size} rows skipped") else ""))
                if (plan.missingPrice > 0) div("small", t("${plan.missingPrice} varer mangler pris (settes til 0, merket «Kontroller»).", "${plan.missingPrice} products have no price (set to 0, marked «Check»)."))
                if (plan.missingVat > 0) div("small", t("${plan.missingVat} varer mangler MVA (settes til 15 %).", "${plan.missingVat} products have no VAT (set to 15 %)."))
            }
            val sample = plan.newProducts.take(8).ifEmpty { plan.updates.take(8).map { it.second } }
            if (sample.isNotEmpty()) div("import-table-wrap") {
                el("table", "import-table") {
                    el("thead") {
                        el("tr") {
                            listOf(t("Varenavn", "Name"), t("Nr.", "No."), t("Hovedvare", "Main category"), t("Enhet", "Unit"), t("Pris", "Price"), t("MVA", "VAT")).forEach { el("th", "", it) }
                        }
                    }
                    el("tbody") {
                        sample.forEach { p ->
                            el("tr") {
                                el("td", "", p.name)
                                el("td", "", p.varenummer)
                                el("td", "", p.category + if (p.undergruppe.isNotBlank()) " · ${p.undergruppe}" else "")
                                el("td", "", p.unit)
                                el("td", "num", formatPrice(p.pricePerUnit))
                                el("td", "num", "${p.vat.toInt()} %")
                            }
                        }
                    }
                }
                if (plan.total > sample.size) div("muted tiny", t("Viser ${sample.size} av ${plan.total}.", "Showing ${sample.size} of ${plan.total}."))
            }
            if (plan.skipped.isNotEmpty()) {
                button(if (state.showSkipped) t("Skjul rader som hoppes over", "Hide skipped rows") else t("Vis rader som hoppes over (${plan.skipped.size})", "Show skipped rows (${plan.skipped.size})"), "btn text small") {
                    state.showSkipped = !state.showSkipped; requestRender()
                }
                if (state.showSkipped) div("import-skipped") {
                    plan.skipped.take(200).forEach { s -> div("small", t("Rad", "Row") + " ${s.sheetRow}: ${s.text.take(70)} – ${s.reason}") }
                }
            }

            dialogActions {
                button(t("Avbryt", "Cancel"), "btn text") { close() }
                val count = plan.newProducts.size + plan.updates.size
                button(if (count == 0) t("Ingenting å importere", "Nothing to import") else t("Importer $count varer", "Import $count products"), "btn primary", enabled = count > 0) {
                    core.importProducts(plan.newProducts, plan.updates.map { it.second }, user, state.fileName)
                    close()
                    productsCategory = null
                    toast(t("${plan.newProducts.size} nye varer og ${plan.updates.size} oppdatert. Hovedvarer: ${plan.categories.joinToString { it.name }}.",
                        "${plan.newProducts.size} new products and ${plan.updates.size} updated. Main categories: ${plan.categories.joinToString { it.name }}."))
                }
            }
        }
    }
}

/** 0 → "A", 27 → "AB". */
internal fun columnLetter(index: Int): String {
    var n = index + 1
    val sb = StringBuilder()
    while (n > 0) { val r = (n - 1) % 26; sb.insert(0, ('A' + r)); n = (n - 1) / 26 }
    return sb.toString()
}
